/**
 * PMC — the robot handshake register, decoded.
 *
 * On a real control this lives under SYSTEM → PMC, and it is where a maintenance engineer goes to
 * ask "what is the cell controller telling this machine right now". The register is one 16-bit word
 * in the R area whose bits are the robot's handshakes with THIS machine.
 *
 * ⚠️ The bit order below MIRRORS `focas-sim/focas/signals.py`, which is the owner. It is a declared
 * contract, not something captured from a real FANUC, and it is pinned by a test on that side. If
 * you change one here without changing it there, the screen will lie about a register that is still
 * being written correctly — which is the worst kind of wrong, because the wire stays right.
 */

// Address the simulator writes to (focasConfig.sig on each line station).
export const PMC_SIG_AREA = 'R';
export const PMC_SIG_ADDRESS = 500;

// bit -> [signal key, what it means to THIS machine]
const BITS = [
  ['ROBOT_INBOUND_PICK', 'ROBOT COMING FOR PART'],
  ['ROBOT_INBOUND_DROP', 'ROBOT COMING TO LOAD'],
  ['ROBOT_PRESENT',      'ROBOT AT DOOR'],
  ['DOOR_OPEN',          'GUARD DOOR OPEN'],
  ['UNLOADING',          'UNLOADING'],
  ['WASH_ACTIVE',        'WASH CYCLE'],
  ['LOADING',            'LOADING'],
  ['CYCLE_ACTIVE',       'CYCLE ACTIVE'],
  ['PART_PRESENT',       'PART PRESENT'],
];

/** Pack the machine's SIG_* state into the same word focas-sim writes, so the screen shows the wire. */
function wordFrom(values) {
  let w = 0;
  BITS.forEach(([key], bit) => { if (values[`SIG_${key}`]) w |= (1 << bit); });
  return w;
}

export default function PmcPage({ values = {} }) {
  const present = BITS.some(([k]) => values[`SIG_${k}`] !== undefined);
  const word = wordFrom(values);

  if (!present) {
    return (
      <div className="fanuc-cell p-6 text-center">
        <div className="fanuc-value font-bold text-sm tracking-widest">PMC</div>
        <div className="fanuc-label text-xs mt-1">NO SIGNAL DATA ON THIS MACHINE</div>
      </div>
    );
  }

  return (
    <div className="fanuc-cell p-3">
      <div className="flex items-baseline justify-between mb-2">
        <span className="fanuc-label text-[10px] uppercase tracking-widest">
          {PMC_SIG_AREA}{PMC_SIG_ADDRESS} · ROBOT I/F
        </span>
        <span className="fanuc-value font-mono font-bold text-lg tabular-nums">
          {String(word).padStart(5, '0')}
          <span className="fanuc-label text-[10px] ml-2">
            H{word.toString(16).toUpperCase().padStart(4, '0')}
          </span>
        </span>
      </div>

      {/* One row per bit — LED, number, name. A real PMC screen is a grid of bits and nothing else. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-[3px]">
        {BITS.map(([key, label], bit) => {
          const on = Boolean(values[`SIG_${key}`]);
          return (
            <div key={key} className="flex items-center gap-2 font-mono text-[11px] leading-tight">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                style={{
                  background: on ? '#22c55e' : 'rgba(255,255,255,0.10)',
                  boxShadow: on ? '0 0 6px rgba(34,197,94,0.8)' : 'none',
                }}
              />
              <span className="fanuc-label opacity-50 w-8 shrink-0">{bit}.{on ? '1' : '0'}</span>
              <span className={on ? 'fanuc-value font-bold truncate' : 'fanuc-label truncate'}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
